// src/pages/ReportesPOS.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📈 REPORTES POS - ESTILO eFIT
// Análisis de ventas y rendimiento con gráficos
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useFiscal } from "../hooks/useFiscal";

const ReportesPOS = () => {
  const fiscal = useFiscal();
  const [reportes, setReportes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    fecha_inicio: "",
    fecha_fin: "",
  });

  useEffect(() => {
    cargarReportes();
  }, []);

  const cargarReportes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.fecha_inicio) params.append("fecha_inicio", filtros.fecha_inicio);
      if (filtros.fecha_fin) params.append("fecha_fin", filtros.fecha_fin);

      const res = await api.get(`/ventas/reportes?${params.toString()}`);
      setReportes(res.data);
    } catch (error) {
      console.error("Error al cargar reportes:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: fiscal.moneda?.codigo || "DOP",
    }).format(monto || 0);
  };

  // Colores eFIT
  const COLORS = ["#1e3a8a", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];

  const ventasPorMetodoFormateadas = reportes?.ventas_por_metodo?.map(item => ({
    metodo_pago: item.metodo_pago || "Sin especificar",
    monto_total: parseFloat(item.monto_total || 0),
    cantidad: parseInt(item.cantidad || 0)
  })) || [];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Cargando reportes...</p>
        </div>
      </div>
    );
  }

  if (!reportes) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorIcon}>⚠️</div>
        <p style={styles.errorText}>No se pudieron cargar los reportes</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            <div style={styles.headerIconBox}>
              <span style={styles.headerIcon}>📈</span>
            </div>
            <div>
              <h1 style={styles.title}>Reportes POS</h1>
              <p style={styles.subtitle}>Análisis de ventas y rendimiento</p>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Filtros */}
        <div style={styles.filtrosCard}>
          <div style={styles.filtrosHeader}>
            <span style={styles.filtrosIcon}>🔍</span>
            <span style={styles.filtrosTitle}>Filtros de análisis</span>
          </div>
          <div style={styles.filtrosGrid}>
            <div style={styles.formGroup}>
              <label style={styles.label}>📅 Fecha Inicio</label>
              <input
                type="date"
                value={filtros.fecha_inicio}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_inicio: e.target.value })
                }
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>📅 Fecha Fin</label>
              <input
                type="date"
                value={filtros.fecha_fin}
                onChange={(e) =>
                  setFiltros({ ...filtros, fecha_fin: e.target.value })
                }
                style={styles.input}
              />
            </div>

            <div style={styles.filtrosActions}>
              <button 
                onClick={cargarReportes} 
                style={styles.btnFiltrar}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                📊 Aplicar Filtros
              </button>
              <button
                onClick={() => {
                  setFiltros({ fecha_inicio: "", fecha_fin: "" });
                  setTimeout(() => cargarReportes(), 100);
                }}
                style={styles.btnLimpiar}
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>

        {/* Tarjetas de Resumen */}
        <div style={styles.statsGrid}>
          <div style={{...styles.statCard, background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)'}}>
            <div style={styles.statIconBox}>📊</div>
            <div style={styles.statInfo}>
              <div style={styles.statLabel}>Ventas Hoy</div>
              <div style={styles.statNumber}>
                {reportes.ventas_hoy?.total_ventas_hoy || 0}
              </div>
              <div style={styles.statMonto}>
                {formatearMoneda(reportes.ventas_hoy?.monto_hoy)}
              </div>
            </div>
          </div>

          <div style={{...styles.statCard, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
            <div style={styles.statIconBox}>💰</div>
            <div style={styles.statInfo}>
              <div style={styles.statLabel}>Total Ventas</div>
              <div style={styles.statNumber}>
                {reportes.resumen?.total_ventas || 0}
              </div>
              <div style={styles.statMonto}>
                {formatearMoneda(reportes.resumen?.monto_total)}
              </div>
            </div>
          </div>

          <div style={{...styles.statCard, background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'}}>
            <div style={styles.statIconBox}>📈</div>
            <div style={styles.statInfo}>
              <div style={styles.statLabel}>Promedio por Venta</div>
              <div style={styles.statNumber}>-</div>
              <div style={styles.statMonto}>
                {formatearMoneda(reportes.resumen?.promedio_venta)}
              </div>
            </div>
          </div>

          <div style={{...styles.statCard, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
            <div style={styles.statIconBox}>💵</div>
            <div style={styles.statInfo}>
              <div style={styles.statLabel}>Ganancia Total</div>
              <div style={styles.statNumber}>-</div>
              <div style={styles.statMonto}>
                {formatearMoneda(reportes.ganancias?.ganancia_total)}
              </div>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div style={styles.chartsGrid}>
          {/* Ventas por Día */}
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>
              <span style={styles.chartIcon}>📅</span>
              Ventas por Día
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={reportes.ventas_por_dia}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="fecha"
                  tickFormatter={(fecha) =>
                    new Date(String(fecha).substring(0,10) + 'T00:00:00').toLocaleDateString("es-DO", {
                      day: "2-digit",
                      month: "2-digit",
                    })
                  }
                  stroke="#64748b"
                />
                <YAxis stroke="#64748b" />
                <Tooltip
                  formatter={(value) => formatearMoneda(value)}
                  labelFormatter={(fecha) =>
                    new Date(String(fecha).substring(0,10) + 'T00:00:00').toLocaleDateString("es-DO")
                  }
                  contentStyle={{
                    background: 'white',
                    border: '2px solid #3b82f6',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  name="Ventas"
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, fill: '#1e3a8a' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Ventas por Método de Pago */}
          <div style={styles.chartCard}>
            <h3 style={styles.chartTitle}>
              <span style={styles.chartIcon}>💳</span>
              Ventas por Método de Pago
            </h3>
            {ventasPorMetodoFormateadas.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ventasPorMetodoFormateadas}
                    dataKey="monto_total"
                    nameKey="metodo_pago"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={50}
                    label={(entry) =>
                      `${entry.metodo_pago}: ${formatearMoneda(entry.monto_total)}`
                    }
                  >
                    {ventasPorMetodoFormateadas.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatearMoneda(value)} 
                    contentStyle={{
                      background: 'white',
                      border: '2px solid #3b82f6',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={styles.noDataBox}>
                <span style={styles.noDataIcon}>📭</span>
                <span>No hay datos de métodos de pago</span>
              </div>
            )}
          </div>
        </div>

        {/* Top Productos */}
        <div style={styles.chartCardFull}>
          <h3 style={styles.chartTitle}>
            <span style={styles.chartIcon}>🔥</span>
            Top 10 Productos Más Vendidos
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={reportes.top_productos} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" stroke="#64748b" />
              <YAxis 
                dataKey="nombre" 
                type="category" 
                width={150}
                stroke="#64748b"
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === "Ingresos" || name === "Ganancia") {
                    return formatearMoneda(value);
                  }
                  return value;
                }}
                contentStyle={{
                  background: 'white',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Legend />
              <Bar dataKey="total_vendido" fill="#1e3a8a" name="Cantidad Vendida" radius={[0, 4, 4, 0]} />
              <Bar dataKey="ingresos" fill="#10b981" name="Ingresos" radius={[0, 4, 4, 0]} />
              <Bar dataKey="ganancia_total" fill="#f59e0b" name="Ganancia" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Ventas por Cajero */}
        <div style={styles.chartCardFull}>
          <h3 style={styles.chartTitle}>
            <span style={styles.chartIcon}>👤</span>
            Ventas por Cajero
          </h3>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Cajero</th>
                  <th style={styles.th}>Nº Ventas</th>
                  <th style={styles.th}>Total Ventas</th>
                  <th style={styles.th}>Promedio</th>
                </tr>
              </thead>
              <tbody>
                {reportes.ventas_por_cajero?.map((cajero, index) => (
                  <tr key={index} style={styles.tableRow}>
                    <td style={styles.td}>
                      <div style={styles.cajeroInfo}>
                        <div style={styles.cajeroAvatar}>
                          {cajero.cajero?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <span style={styles.cajeroNombre}>{cajero.cajero}</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.ventasCount}>{cajero.num_ventas}</span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.ventasTotal}>
                        {formatearMoneda(cajero.total_ventas)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {formatearMoneda(cajero.promedio_por_venta)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Estilos de animación */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT - PALETA AZUL CORPORATIVA
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
  },
  loadingContent: {
    textAlign: "center",
    color: "white",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid rgba(255,255,255,0.3)",
    borderTopColor: "white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto 20px",
  },
  loadingText: {
    fontSize: "16px",
    opacity: "0.9",
  },
  errorContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#ef4444",
  },
  errorIcon: {
    fontSize: "64px",
    marginBottom: "16px",
  },
  errorText: {
    fontSize: "18px",
    fontWeight: "600",
  },

  // Header
  header: {
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    padding: "28px 40px",
    boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
  },
  headerContent: {
    maxWidth: "1600px",
    margin: "0 auto",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  headerIconBox: {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  headerIcon: {
    fontSize: "36px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "800",
    color: "white",
    margin: "0",
  },
  subtitle: {
    fontSize: "15px",
    color: "rgba(255, 255, 255, 0.85)",
    margin: "6px 0 0 0",
  },

  // Main Content
  mainContent: {
    padding: "32px 40px",
    maxWidth: "1600px",
    margin: "0 auto",
  },

  // Filtros
  filtrosCard: {
    background: "white",
    borderRadius: "18px",
    padding: "24px",
    marginBottom: "28px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  filtrosHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  filtrosIcon: {
    fontSize: "20px",
  },
  filtrosTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
  },
  filtrosGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    alignItems: "end",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "8px",
  },
  input: {
    padding: "12px 14px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    background: "#f8fafc",
  },
  filtrosActions: {
    display: "flex",
    gap: "10px",
  },
  btnFiltrar: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  },
  btnLimpiar: {
    padding: "12px 24px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
  },

  // Stats Grid
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
    marginBottom: "28px",
  },
  statCard: {
    borderRadius: "20px",
    padding: "28px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    color: "white",
  },
  statIconBox: {
    fontSize: "40px",
    background: "rgba(255, 255, 255, 0.2)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "16px",
  },
  statInfo: {
    flex: 1,
  },
  statLabel: {
    fontSize: "13px",
    opacity: "0.9",
    marginBottom: "6px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  statNumber: {
    fontSize: "32px",
    fontWeight: "800",
    marginBottom: "4px",
  },
  statMonto: {
    fontSize: "16px",
    fontWeight: "600",
    opacity: "0.95",
  },

  // Charts
  chartsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
    gap: "24px",
    marginBottom: "24px",
  },
  chartCard: {
    background: "white",
    borderRadius: "20px",
    padding: "28px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  chartCardFull: {
    background: "white",
    borderRadius: "20px",
    padding: "28px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    marginBottom: "24px",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  chartTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "20px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "24px",
    margin: "0 0 24px 0",
  },
  chartIcon: {
    fontSize: "24px",
  },
  noDataBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px",
    color: "#64748b",
    gap: "12px",
  },
  noDataIcon: {
    fontSize: "48px",
  },

  // Table
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
  },
  th: {
    padding: "16px 20px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "700",
    color: "#1e3a8a",
    borderBottom: "2px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    borderBottom: "1px solid #f1f5f9",
    transition: "all 0.2s ease",
  },
  td: {
    padding: "16px 20px",
    fontSize: "14px",
    color: "#1e293b",
  },
  cajeroInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  cajeroAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "700",
    fontSize: "16px",
  },
  cajeroNombre: {
    fontWeight: "600",
  },
  ventasCount: {
    display: "inline-block",
    padding: "6px 14px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    color: "#1e3a8a",
    borderRadius: "20px",
    fontWeight: "700",
    fontSize: "13px",
  },
  ventasTotal: {
    fontWeight: "700",
    color: "#059669",
    fontSize: "16px",
  },
};

export default ReportesPOS;